import { useState, useEffect } from "react";
import useInterval from "../../../utils/functions/useInterval";
import { API, graphqlOperation } from "aws-amplify";
import gql from "graphql-tag";

const PLAY_SPEED = 1000; // 1 second
const REFETCH_SPEED = 5000; // 5 seconds
const DATA_LENGTH_LIMIT = 600;

//QUERY FUNCTION TO GET DATA HERE
async function queryMeasurements(
  timestamp: Date,
  deploymentIds: number[],
  offset: number,
  weights: { m: number; d: number; f: number },
  defaults: { m: number; d: number; f: number }
): Promise<Measurement[]> {
  // const timeEnd = new Date(timestamp.getTime() + 1000 * 1800); //30 mins WORTH OF DATA EVERY TIME
  const timeEnd = new Date(); //Replaced this for now since timestamps in database are wrong
  const fromTime = timestamp.toISOString();
  const toTime = timeEnd.toISOString();

  const query = gql`
    query MEASUREMENTS($input: MeasurementsInput!) {
      measurements(input: $input) {
        timestamp
        heatmap
        density
        mood
        flow
        image
      }
    }
  `;

  //QUERY AND RETURN DATA
  const res = await API.graphql(
    graphqlOperation(query, {
      input: {
        deploymentIds,
        tableNames: [
          "Densities",
          "Moods",
          "Flows",
          "Images",
          "DensityHeatmaps",
        ],
        fromTime,
        toTime,
        offset,
      },
    })
  );
  console.log(res);

  //@ts-ignore
  const results = res ? (res.data.measurements as Measurement[]) : [];
  //Fill in measurements with default values
  return results.map(r => ({
    ...r,
    density: r.density * weights.d || defaults.d + Math.random() * 0.2,
    flow: r.flow * weights.f || defaults.f + Math.random() * 0.2,
    mood: r.mood * weights.m || defaults.m + Math.random() * 0.2,
  }));
}

async function queryDeployment(deploymentId: number): Promise<Deployment> {
  //QUERY HERE AND RETURN DATA
  return { location: "" };
}

export type MeasurementData = {
  loading: boolean;
  data: Measurement[];
  slice: Measurement;
  play: boolean;
  count: number;
};
export type MeasurementSetters = {
  setTime: (
    timestamp: Date,
    offset: number,
    rewrite?: boolean
  ) => Promise<void>;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  setSlice: React.Dispatch<React.SetStateAction<Measurement>>;
  setPlay: React.Dispatch<React.SetStateAction<boolean>>;
};
export type Measurement = {
  mood: number;
  density: number;
  flow: number;
  timestamp: number;
  image: string;
  heatmap: string;
};
export type Deployment = {
  location: string;
};
export const useMeasurement = (
  deploymentIds: number[],
  initialTimestamp: Date,
  weights: { m: number; d: number; f: number },
  defaults: { m: number; d: number; f: number }
) => {
  const initialData: Measurement[] = [
    {
      mood: 0,
      density: 0,
      flow: 0,
      timestamp: new Date().getTime(),
      image: "",
      heatmap: "",
    },
  ];

  //STATE
  const [loading, setLoading] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [data, setData] = useState(initialData);
  const [slice, setSlice] = useState(initialData[0]);
  const [count, setCount] = useState(0);
  const [play, setPlay] = useState(true);
  const [prevAvaiable, setPrevAvaiable] = useState({
    i: initialData[0].image,
    h: initialData[0].heatmap,
    m: 0,
    d: 0,
    f: 0,
  });

  //INITIAL FETCH
  useEffect(() => {
    setTime(initialTimestamp, 0);
    setRefetch(true);
  }, []);

  //USE INTERVAL TO REFETCH
  useInterval(
    () => {
      const refetch = async () => {
        setRefetch(false);
        await setTime(initialTimestamp, data.length);
        setRefetch(true);
      };
      refetch();
    },
    refetch ? REFETCH_SPEED : null
  );

  //Use an interval here
  useInterval(
    () => {
      //Make sure count does not exceed the data arrays length
      if (count < data.length - 1) {
        setCount(count + 1);
      }
    },
    play ? PLAY_SPEED : null
  );

  //Use to set the current slice based on count
  useEffect(() => {
    const slice = data.length > count ? data[count] : initialData[0];

    //Set previously avaiable image if none in new slice: probably not the most efficient way
    setSlice({
      ...slice,
      mood: prevAvaiable.m,
      density: prevAvaiable.d,
      flow: prevAvaiable.f,
      image: prevAvaiable.i,
      heatmap: prevAvaiable.h,
    });

    let newPrevAvaiable = prevAvaiable;
    if (slice.image)
      newPrevAvaiable = { ...prevAvaiable, i: slice.image, h: slice.heatmap };
    if (slice.mood) newPrevAvaiable = { ...newPrevAvaiable, m: slice.mood };
    if (slice.density) {
      newPrevAvaiable = { ...newPrevAvaiable, d: slice.density };
    }
    if (slice.flow) newPrevAvaiable = { ...newPrevAvaiable, f: slice.flow };
    setPrevAvaiable(newPrevAvaiable);
  }, [count]);

  //FUNCTIONS
  const setTime = async (
    timestamp: Date,
    offset: number,
    rewrite: boolean = false
  ) => {
    //Refetches the data based on given timestamp
    setLoading(true);

    let newData;
    if (data.length > 1) {
      //Find largest timestamp for density

      newData = await queryMeasurements(
        // new Date(data[data.length - 1].timestamp),
        new Date(getLatestDataTimestamp(data)),
        deploymentIds,
        offset,
        weights,
        defaults
      ); //Based on last timestmap
    } else {
      newData = await queryMeasurements(
        timestamp,
        deploymentIds,
        offset,
        weights,
        defaults
      ); //Based on offset
    }
    if (rewrite) {
      setData(data);
    } else {
      const dataArray = data.concat(newData);
      //If theres too much data stored, dump some of it and change pointer as well
      // if (data.length > DATA_LENGTH_LIMIT) {
      //   setData(data.concat(newData).slice(newData.length));
      //   setCount(count - newData.length);
      // } else {
      setData(dataArray);
      // }
    }
    // setSlice(newData.length > 0 ? newData[0] : initialData[0]);
    setLoading(false);
  };

  const measurementData = { loading, data, slice, play, count };
  const setters = { setTime, setSlice, setPlay, setCount };

  const hooks: [MeasurementData, MeasurementSetters] = [
    measurementData,
    setters,
  ];
  return hooks;
};

export const useDeployment = (deploymentId: number) => {
  const initialDeployment = {
    location: "",
  };
  const [loading, setLoading] = useState(false);
  const [deployment, setDeployment] = useState(initialDeployment);

  //FUNCTION TO GET DEPLOYMENT DETAILS
  useEffect(() => {}, []);

  return [{ deployment, loading }];
};

//This function needs to be fixed
export function getLatestDataTimestamp(data: Measurement[]) {
  const maxIndex = data.length - 1;
  if (data.length > 0) {
    for (let i = maxIndex; maxIndex > 0; i--) {
      if (data[i] && data[i].image) {
        //If it has image means it has density reading
        return data[i].timestamp;
      }
    }
  }
  return data[maxIndex].timestamp; //Fallback to last timestamp
}
